import { memo, useMemo } from 'react'
import { motion, type Transition, type Variants } from 'framer-motion'
import type { StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import type { ChatMessage, ToolCall, UserMessage } from '../../../../types'
import type { PendingAttachmentStagedAction } from '../../../../types/attachments'
import { useStagedActionsOwnedByPriorTurn } from '../../latest-staged-actions-context'
import { StagedActionsRenderer, type StagedActionSnapshot } from './staged-actions-renderer'
import { StagedActionsSection } from './staged-actions-section'
import { SkillPreferenceActionsSection } from './skill-preference-actions-section'
import { RemindersSection } from './reminders-section'

const emptyToolCalls: Array<ToolCall> = []

// Cards stagger in alongside the block reveal (which unlocks the gate at
// reveal start) instead of popping in all at once after it finishes.
const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { delayChildren: 0.15, staggerChildren: 0.08 } },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 4 },
  visible: { opacity: 1, y: 0 },
}

const itemTransition: Transition = { duration: 0.18, ease: 'easeOut' }

// ---------------------------------------------------------------------------
// Staged-action preview on the latest user message
// ---------------------------------------------------------------------------

export const StagedActionPreview = memo(function StagedActionPreview({
  message,
  isLastUserGroup,
  isTurnActive,
  onStagedActionClick,
}: {
  message: UserMessage
  isLastUserGroup?: boolean
  isTurnActive: boolean
  onStagedActionClick: (actionId: string, actionType: string) => void
}) {
  const stagedActionsOwnedByPriorTurn = useStagedActionsOwnedByPriorTurn()
  const pendingContext = message.pending_context

  const items = useMemo(() => {
    // Show the preview only on the last user message while the turn is active.
    // Once the turn completes the tool call result card takes over.
    if (!isLastUserGroup || !isTurnActive || !pendingContext) return []
    return (
      pendingContext
        .filter((a) => a.pending?.type === 'stagedAction')
        .map((a): StagedActionSnapshot => {
          const p = a.pending as PendingAttachmentStagedAction
          // The pending attachment's view is a preview subset — map it onto a
          // full StagedActionView explicitly. `supportsUndo: false` because a
          // pending preview never exposes undo; the live action's view takes
          // over once the staged-actions query catches up (`liveAction ?? action`).
          const view: StagedActionView | undefined = p.view
            ? {
                title: p.view.title,
                icon: p.view.icon,
                content: p.view.content ?? '',
                tags: p.view.tags ?? [],
                buttons: [],
                invokeLabel: p.view.invokeLabel ?? '',
                supportsUndo: false,
              }
            : undefined
          return { id: p.actionId, actionType: p.actionType, title: p.title, toolSet: p.toolSet, view }
        })
        // Suppress the preview only when a prior turn already renders a completed
        // card for this action — otherwise it duplicates beneath the latest user
        // message. Actions created by the active turn are not in this set, so the
        // preview keeps bridging until that turn's card reveals (no mid-turn gap).
        // See latest-staged-actions-context.ts.
        .filter((item) => !stagedActionsOwnedByPriorTurn?.has(item.id))
    )
  }, [pendingContext, isTurnActive, isLastUserGroup, stagedActionsOwnedByPriorTurn])

  if (items.length === 0) return null

  return <StagedActionsRenderer actions={items} isLast={true} onStagedActionClick={onStagedActionClick} />
})

// ---------------------------------------------------------------------------
// Post-message artifacts (staged actions, reminders) and their reveal gate
// ---------------------------------------------------------------------------

export interface MessageArtifactsProps {
  message: ChatMessage
  groupToolCalls?: Array<ToolCall>
  isMostRecent?: boolean
  /** Drives the reveal gate — flips false in the same commit the reveal starts. */
  isTurnActive: boolean
  onStagedActionClick?: (actionId: string, actionType: string) => void
  onStagedActionCreated?: () => void
  onReminderClick?: (reminderId: string) => void
}

export const MessageArtifacts = memo(function MessageArtifacts({
  message,
  groupToolCalls,
  isMostRecent,
  isTurnActive,
  onStagedActionClick,
  onStagedActionCreated,
  onReminderClick,
}: MessageArtifactsProps) {
  if (!onStagedActionClick && !onReminderClick) return null

  const toolCalls = message.role === 'assistant' ? (message.toolCalls ?? emptyToolCalls) : emptyToolCalls

  // Gate post-message artifacts on the turn ending — the block reveal starts in
  // the same commit `isTurnActive` flips false, so the cards stagger in
  // alongside the answer (delayChildren/staggerChildren on the container).
  // History loads and non-most-recent messages skip the gate entirely.
  const gateApplies = message.role === 'assistant' && !!isMostRecent
  const showArtifacts = !gateApplies || !isTurnActive

  return (
    <motion.div
      variants={containerVariants}
      initial={gateApplies ? 'hidden' : 'visible'}
      animate={showArtifacts ? 'visible' : 'hidden'}
      className="contents"
    >
      {onStagedActionClick && (
        <motion.div variants={itemVariants} transition={itemTransition}>
          <StagedActionsSection
            toolCalls={toolCalls}
            groupToolCalls={groupToolCalls}
            isTurnActive={isTurnActive}
            onStagedActionClick={onStagedActionClick}
            onStagedActionCreated={onStagedActionCreated}
          />
          <SkillPreferenceActionsSection toolCalls={toolCalls} groupToolCalls={groupToolCalls} />
        </motion.div>
      )}

      {onReminderClick && (
        <motion.div variants={itemVariants} transition={itemTransition}>
          <RemindersSection
            toolCalls={toolCalls}
            groupToolCalls={groupToolCalls}
            isTurnActive={isTurnActive}
            onReminderClick={onReminderClick}
          />
        </motion.div>
      )}
    </motion.div>
  )
})
